import { useState, useEffect, useCallback } from 'react';
import {
  WineType,
  type WineFormData,
  type WineStatus,
  type WineAvailability,
} from '@wineryxplorer/shared';
import { WineBasicInfoFields } from './WineForm/WineBasicInfoFields';
import { WineTechnicalFields } from './WineForm/WineTechnicalFields';
import { WineDetailFields } from './WineForm/WineDetailFields';
import { WineDrinkingWindowFields } from './WineForm/WineDrinkingWindowFields';
import { WineProfessionalScoresFields } from './WineForm/WineProfessionalScoresFields';
import { WineManagementFields } from './WineForm/WineManagementFields';
import type { WineFormErrors, ProfessionalScoreEntry } from './WineForm/types';

export interface WineFormProps {
  initialData?: Partial<WineFormData>;
  onSubmit: (data: WineFormData) => Promise<void>;
  onCancel: () => void;
  venueId?: string;
  isSubmitting?: boolean;
  hideButtons?: boolean;
  submitRef?: React.MutableRefObject<(() => Promise<void>) | null>;
}

export const WineForm = ({
  initialData,
  onSubmit,
  onCancel,
  venueId: propVenueId,
  isSubmitting = false,
  hideButtons = false,
  submitRef,
}: WineFormProps) => {
  // Form state - Basic Information
  const [name, setName] = useState(initialData?.name || '');
  const [venueId, setVenueId] = useState(initialData?.venueId || propVenueId || '');
  const [vintage, setVintage] = useState(initialData?.vintage?.toString() || '');
  const [wineType, setWineType] = useState<WineType>(initialData?.wineType || WineType.RED);
  const [varietal, setVarietal] = useState(initialData?.varietal || '');
  const [description, setDescription] = useState(initialData?.description || '');

  // Form state - Media (read-only from initialData, managed by WineMediaManager)
  const [heroImageId] = useState<string | undefined>(initialData?.heroImageId);
  const [imageIds] = useState<string[]>(initialData?.imageIds || []);

  // Form state - Technical Details
  const [alcoholContent, setAlcoholContent] = useState(initialData?.alcoholContent?.toString() || '');
  const [price, setPrice] = useState(initialData?.price?.toString() || '');
  const [volume, setVolume] = useState(initialData?.volume?.toString() || '');
  const [countryOfOrigin, setCountryOfOrigin] = useState(initialData?.countryOfOrigin || 'USA');
  
  // Form state - Wine Details
  const [appellation, setAppellation] = useState(initialData?.appellation || '');
  const [subAppellation, setSubAppellation] = useState(initialData?.subAppellation || '');
  const [descriptors, setDescriptors] = useState(initialData?.descriptors?.join(', ') || '');
  const [availability, setAvailability] = useState<WineAvailability>(
    initialData?.availability || 'unknown'
  );
  const [holdUntil, setHoldUntil] = useState(initialData?.holdUntil?.toString() || '');
  const [drinkBefore, setDrinkBefore] = useState(initialData?.drinkBefore?.toString() || '');
  const [appellationNotes, setAppellationNotes] = useState(initialData?.appellationNotes || '');
  const [allergens, setAllergens] = useState(initialData?.allergens || '');
  const [grapes, setGrapes] = useState(initialData?.grapes || '');
  
  // Form state - Professional Scores (array of { criticName: score })
  const [professionalScores, setProfessionalScores] = useState<ProfessionalScoreEntry[]>(
    initialData?.professionalScores?.map(scoreObj => {
      const [critic, score] = Object.entries(scoreObj)[0];
      return { critic, score: score.toString() };
    }) || []
  );

  // Form state - Management
  const [status, setStatus] = useState<WineStatus>(initialData?.status || 'draft');

  // Validation errors
  const [errors, setErrors] = useState<WineFormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Update venueId when prop changes
  useEffect(() => {
    if (propVenueId && !venueId) {
      setVenueId(propVenueId);
    }
  }, [propVenueId, venueId]);



  const validateForm = useCallback((): boolean => {
    const newErrors: WineFormErrors = {};

    // Required fields
    if (!name.trim()) {
      newErrors.name = 'Wine name is required';
    } else if (name.length > 200) {
      newErrors.name = 'Wine name must be 200 characters or less';
    }

    if (!countryOfOrigin.trim()) {
      newErrors.countryOfOrigin = 'Country of origin is required';
    }

    // Validate vintage if provided
    if (vintage) {
      const vintageNum = parseInt(vintage, 10);
      const currentYear = new Date().getFullYear();
      if (isNaN(vintageNum) || vintageNum < 1800 || vintageNum > currentYear + 1) {
        newErrors.vintage = `Vintage must be between 1800 and ${currentYear + 1}`;
      }
    }

    // Validate alcohol content if provided
    if (alcoholContent) {
      const alcoholNum = parseFloat(alcoholContent);
      if (isNaN(alcoholNum) || alcoholNum < 0 || alcoholNum > 100) {
        newErrors.alcoholContent = 'Alcohol content must be between 0 and 100';
      }
    }

    // Validate price if provided
    if (price) {
      const priceNum = parseFloat(price);
      if (isNaN(priceNum) || priceNum < 0) {
        newErrors.price = 'Price must be a non-negative number';
      }
    }

    // Validate volume if provided
    if (volume) {
      const volumeNum = parseFloat(volume);
      if (isNaN(volumeNum) || volumeNum < 0) {
        newErrors.volume = 'Volume must be a non-negative number';
      }
    }

    // Validate drinking window
    if (holdUntil && drinkBefore) {
      const holdUntilNum = parseInt(holdUntil, 10);
      const drinkBeforeNum = parseInt(drinkBefore, 10);
      if (!isNaN(holdUntilNum) && !isNaN(drinkBeforeNum) && holdUntilNum > drinkBeforeNum) {
        newErrors.holdUntil = 'Hold until year must not be greater than drink before year';
        newErrors.drinkBefore = 'Drink before year must not be less than hold until year';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [name, countryOfOrigin, vintage, alcoholContent, price, volume, holdUntil, drinkBefore]);

  const handleBlur = (field: string) => {
    setTouched({ ...touched, [field]: true });
  };

  const handleSubmit = useCallback(async () => {
    // Mark all fields as touched
    setTouched({
      name: true,
      wineType: true,
      countryOfOrigin: true,
      vintage: true,
      alcoholContent: true,
      price: true,
      volume: true,
      holdUntil: true,
      drinkBefore: true,
    });

    if (!validateForm()) {
      return;
    }

    // Convert professional scores to the correct format
    const formattedScores: Record<string, number>[] = professionalScores
      .filter((ps) => ps.critic.trim() && ps.score.trim())
      .map((ps) => ({ [ps.critic.trim()]: parseFloat(ps.score) }));

    const formData: WineFormData = {
      name: name.trim(),
      venueId: venueId || '',
      vintage: vintage ? parseInt(vintage, 10) : undefined,
      wineType,
      varietal: varietal.trim() || undefined,
      description: description.trim() || undefined,
      alcoholContent: alcoholContent ? parseFloat(alcoholContent) : undefined,
      price: price ? parseFloat(price) : undefined,
      volume: volume ? parseFloat(volume) : undefined,
      heroImageId,
      imageIds,
      countryOfOrigin: countryOfOrigin.trim(),
      appellation: appellation.trim() || undefined,
      subAppellation: subAppellation.trim() || undefined,
      descriptors: descriptors
        .split(',')
        .map((d) => d.trim())
        .filter((d) => d.length > 0),
      availability,
      holdUntil: holdUntil ? parseInt(holdUntil, 10) : undefined,
      drinkBefore: drinkBefore ? parseInt(drinkBefore, 10) : undefined,
      professionalScores: formattedScores,
      appellationNotes: appellationNotes.trim() || undefined,
      allergens: allergens.trim() || undefined,
      grapes: grapes.trim() || undefined,
      status,
    };

    await onSubmit(formData);
  }, [
    name,
    venueId,
    vintage,
    wineType,
    varietal,
    description,
    heroImageId,
    imageIds,
    alcoholContent,
    price,
    volume,
    countryOfOrigin,
    appellation,
    subAppellation,
    descriptors,
    availability,
    holdUntil,
    drinkBefore,
    professionalScores,
    appellationNotes,
    allergens,
    grapes,
    status,
    onSubmit,
    validateForm,
  ]);

  // Expose submit handler to parent via ref
  useEffect(() => {
    if (submitRef) {
      submitRef.current = handleSubmit;
    }
  }, [submitRef, handleSubmit]);

  return (
    <div>
      <WineBasicInfoFields
        name={name}
        onNameChange={setName}
        wineType={wineType}
        onWineTypeChange={setWineType}
        vintage={vintage}
        onVintageChange={setVintage}
        varietal={varietal}
        onVarietalChange={setVarietal}
        countryOfOrigin={countryOfOrigin}
        onCountryOfOriginChange={setCountryOfOrigin}
        description={description}
        onDescriptionChange={setDescription}
        errors={errors}
        touched={touched}
        onBlur={handleBlur}
        isSubmitting={isSubmitting}
      />

      <WineTechnicalFields
        alcoholContent={alcoholContent}
        onAlcoholContentChange={setAlcoholContent}
        price={price}
        onPriceChange={setPrice}
        volume={volume}
        onVolumeChange={setVolume}
        availability={availability}
        onAvailabilityChange={setAvailability}
        errors={errors}
        touched={touched}
        onBlur={handleBlur}
        isSubmitting={isSubmitting}
      />

      <WineDetailFields
        appellation={appellation}
        onAppellationChange={setAppellation}
        subAppellation={subAppellation}
        onSubAppellationChange={setSubAppellation}
        descriptors={descriptors}
        onDescriptorsChange={setDescriptors}
        grapes={grapes}
        onGrapesChange={setGrapes}
        allergens={allergens}
        onAllergensChange={setAllergens}
        appellationNotes={appellationNotes}
        onAppellationNotesChange={setAppellationNotes}
        isSubmitting={isSubmitting}
      />

      <WineDrinkingWindowFields
        holdUntil={holdUntil}
        onHoldUntilChange={setHoldUntil}
        drinkBefore={drinkBefore}
        onDrinkBeforeChange={setDrinkBefore}
        errors={errors}
        touched={touched}
        onBlur={handleBlur}
        isSubmitting={isSubmitting}
      />

      <WineProfessionalScoresFields
        professionalScores={professionalScores}
        onProfessionalScoresChange={setProfessionalScores}
        isSubmitting={isSubmitting}
      />

      <WineManagementFields
        status={status}
        onStatusChange={setStatus}
        isSubmitting={isSubmitting}
      />

      {/* Form Actions */}
      {!hideButtons && (
        <div className="mt-6 flex gap-4 border-t border-gray-200 pt-6">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`flex-1 px-6 py-3 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-info-500 focus:ring-offset-2 ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting ? 'Saving...' : 'Save Wine'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className={`px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-info-500 focus:ring-offset-2 ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};
